import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { COLORS, Btn, Icons, Spinner, useToast } from './ui/crm-ui';
import GenerateDocumentModal, { type PromptForGeneration } from './views/modals/GenerateDocumentModal';

interface PromptTemplate {
  prompt_id: number;
  prompt_name: string;
  category: string | null;
  division: string | null;
  token_sensitivity: string | null;
  requires_document_extraction: boolean;
  token_estimate: number | null;
  has_document_output?: boolean;
  output_type?: string | null;
  document_template_id?: number | null;
  output_filename_prefix?: string | null;
}

interface DocumentItem {
  itemId: string;
  name: string;
  fileType?: string;
}

interface AssistResult {
  response: string;
  promptName: string;
  documentsProcessed: number;
  tokens: number;
}

interface Props {
  entityId: number | null;
  jobId?: number | null;
  entityEngagementId?: number | null;
  documents: DocumentItem[];
  baseApiUrl: string;
  onClose: () => void;
}

type Stage = 'pick-template' | 'pick-docs' | 'run';

function apiError(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e.response?.data?.error ?? e.message ?? 'An error occurred';
}

function sensitivityColor(s: string | null): string {
  if (s === 'High') return COLORS.orange;
  if (s === 'Medium') return COLORS.blue;
  return COLORS.green;
}

function sensitivityBg(s: string | null): string {
  if (s === 'High') return COLORS.orangeSoft;
  if (s === 'Medium') return COLORS.blueSoft;
  return COLORS.greenSoft;
}

export default function DocumentAssistancePanel({
  entityId,
  jobId,
  entityEngagementId,
  documents,
  baseApiUrl,
  onClose,
}: Props) {
  const [stage, setStage] = useState<Stage>('pick-template');

  // Stage 1 state
  const [prompts, setPrompts] = useState<PromptTemplate[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [promptsError, setPromptsError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  // Stage 2 state
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);

  // Stage 3 state
  const [customCommand, setCustomCommand] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<AssistResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  // Generate Document modal state
  const [generatePrompt, setGeneratePrompt] = useState<PromptForGeneration | null>(null);
  const { show: showToast, node: toastNode } = useToast();

  useEffect(() => {
    let cancelled = false;
    setPromptsLoading(true);
    setPromptsError(null);
    axios
      .get<PromptTemplate[]>(`${baseApiUrl}/api/ai/prompts`)
      .then((res) => {
        if (!cancelled) {
          setPrompts(res.data);
          setPromptsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPromptsError(apiError(err));
          setPromptsLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [baseApiUrl, retryKey]);

  function selectTemplate(p: PromptTemplate) {
    setSelectedTemplate(p);
    setSelectedDocIds([]);
    setStage('pick-docs');
  }

  function toggleDoc(itemId: string) {
    setSelectedDocIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
  }

  async function runAssist() {
    if (!selectedTemplate || !customCommand.trim() || selectedDocIds.length === 0) return;
    setRunning(true);
    setRunError(null);
    setResult(null);
    try {
      const { data } = await axios.post<AssistResult>(`${baseApiUrl}/api/ai/assist`, {
        promptId: selectedTemplate.prompt_id,
        itemIds: selectedDocIds,
        customCommand: customCommand.trim(),
        ...(entityId != null ? { entityId } : {}),
        ...(jobId != null ? { jobId } : {}),
        ...(entityEngagementId != null ? { entityEngagementId } : {}),
      });
      setResult(data);
    } catch (err: unknown) {
      setRunError(apiError(err));
    } finally {
      setRunning(false);
    }
  }

  function startOver() {
    setStage('pick-template');
    setSelectedTemplate(null);
    setSelectedDocIds([]);
    setCustomCommand('');
    setResult(null);
    setRunError(null);
  }

  return (
    <div
      className="crm-anim-fadeUp"
      style={{
        background: COLORS.white,
        borderRadius: 14,
        border: `1px solid ${COLORS.border}`,
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        marginBottom: 18,
        overflow: 'hidden',
      }}
    >
      {/* Panel header */}
      <div
        style={{
          padding: '14px 18px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {Icons.agent(COLORS.blue)}
          <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.dark }}>
            Document Assistance
          </span>
        </div>
        <Btn small onClick={onClose}>Close</Btn>
      </div>

      <div style={{ padding: 18 }}>

        {/* ── Stage 1: Template picker ─────────────────────────────────────── */}
        {stage === 'pick-template' && (
          <>
            <div style={{ fontSize: 13, color: COLORS.mid, marginBottom: 14 }}>
              Select a template to get started
            </div>

            {promptsLoading && <Spinner />}

            {promptsError && (
              <div
                style={{
                  padding: '10px 14px',
                  background: COLORS.redSoft,
                  border: `1px solid ${COLORS.red}30`,
                  borderRadius: 8,
                  color: COLORS.red,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <span>{promptsError}</span>
                <Btn small onClick={() => setRetryKey((k) => k + 1)}>Retry</Btn>
              </div>
            )}

            {!promptsLoading && !promptsError && prompts.length === 0 && (
              <div style={{ fontSize: 13, color: COLORS.mid }}>No templates available.</div>
            )}

            {!promptsLoading && prompts.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: 12,
                }}
              >
                {prompts.map((p) => {
                  const canGenerate = !!p.has_document_output && p.document_template_id != null;
                  return (
                    <div
                      key={p.prompt_id}
                      onClick={() => selectTemplate(p)}
                      style={{
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 10,
                        padding: 14,
                        cursor: 'pointer',
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLDivElement).style.borderColor = COLORS.blue;
                        (e.currentTarget as HTMLDivElement).style.background = COLORS.blueSoft;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLDivElement).style.borderColor = COLORS.border;
                        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: COLORS.dark,
                          marginBottom: 3,
                        }}
                      >
                        {p.prompt_name}
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.mid, marginBottom: 10 }}>
                        {p.category}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: canGenerate ? 10 : 0 }}>
                        {p.division && (
                          <span
                            style={{
                              fontSize: 10,
                              padding: '2px 7px',
                              borderRadius: 5,
                              background: COLORS.light,
                              color: COLORS.mid,
                              fontWeight: 600,
                            }}
                          >
                            {p.division}
                          </span>
                        )}
                        {p.token_sensitivity && (
                          <span
                            style={{
                              fontSize: 10,
                              padding: '2px 7px',
                              borderRadius: 5,
                              background: sensitivityBg(p.token_sensitivity),
                              color: sensitivityColor(p.token_sensitivity),
                              fontWeight: 600,
                            }}
                          >
                            {p.token_sensitivity} sensitivity
                          </span>
                        )}
                        {canGenerate && (
                          <span
                            style={{
                              fontSize: 10,
                              padding: '2px 7px',
                              borderRadius: 5,
                              background: COLORS.greenSoft,
                              color: COLORS.green,
                              fontWeight: 600,
                            }}
                          >
                            {(p.output_type ?? 'document').toUpperCase()} output
                          </span>
                        )}
                      </div>
                      {canGenerate && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Btn
                            primary
                            small
                            onClick={() =>
                              setGeneratePrompt({
                                prompt_id: p.prompt_id,
                                prompt_name: p.prompt_name,
                                output_filename_prefix: p.output_filename_prefix ?? null,
                              })
                            }
                          >
                            Generate Document…
                          </Btn>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Stage 2: Document selector ───────────────────────────────────── */}
        {stage === 'pick-docs' && selectedTemplate && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <Btn small onClick={() => setStage('pick-template')}>← Back</Btn>
              <span style={{ fontWeight: 600, fontSize: 14, color: COLORS.dark }}>
                {selectedTemplate.prompt_name}
              </span>
            </div>

            <div style={{ fontSize: 13, color: COLORS.mid, marginBottom: 12 }}>
              Select the documents to analyse
            </div>

            {documents.length === 0 ? (
              <div style={{ fontSize: 13, color: COLORS.mid }}>No documents in this folder.</div>
            ) : (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}
              >
                {documents.map((d) => {
                  const checked = selectedDocIds.includes(d.itemId);
                  return (
                    <label
                      key={d.itemId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: `1px solid ${checked ? COLORS.blue : COLORS.border}`,
                        background: checked ? COLORS.blueSoft : COLORS.white,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDoc(d.itemId)}
                        style={{
                          accentColor: COLORS.blue,
                          width: 15,
                          height: 15,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: 13, color: COLORS.dark, flex: 1 }}>{d.name}</span>
                      {d.fileType && (
                        <span
                          style={{
                            fontSize: 10,
                            textTransform: 'uppercase',
                            color: COLORS.mid,
                            fontWeight: 600,
                          }}
                        >
                          {d.fileType}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 12, color: COLORS.mid }}>
                {selectedDocIds.length} document{selectedDocIds.length !== 1 ? 's' : ''} selected
              </span>
              <Btn
                primary
                small
                disabled={selectedDocIds.length === 0}
                onClick={() => setStage('run')}
              >
                Continue →
              </Btn>
            </div>
          </>
        )}

        {/* ── Stage 3: Command input and result ────────────────────────────── */}
        {stage === 'run' && selectedTemplate && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <Btn
                small
                onClick={() => {
                  setResult(null);
                  setRunError(null);
                  setStage('pick-docs');
                }}
              >
                ← Back
              </Btn>
              <span style={{ fontWeight: 600, fontSize: 14, color: COLORS.dark }}>
                {selectedTemplate.prompt_name}
              </span>
              <span style={{ fontSize: 12, color: COLORS.mid }}>
                · {selectedDocIds.length} document{selectedDocIds.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: COLORS.mid,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  marginBottom: 6,
                }}
              >
                Instruction
              </div>
              <textarea
                value={customCommand}
                onChange={(e) => setCustomCommand(e.target.value)}
                placeholder="Type your instruction here..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  color: COLORS.dark,
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Btn
                primary
                small
                disabled={!customCommand.trim() || running}
                onClick={runAssist}
              >
                {running ? 'Running...' : 'Run'}
              </Btn>
              <Btn small onClick={startOver}>Start Over</Btn>
            </div>

            {runError && (
              <div
                style={{
                  padding: '10px 14px',
                  background: COLORS.redSoft,
                  border: `1px solid ${COLORS.red}30`,
                  borderRadius: 8,
                  color: COLORS.red,
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                {runError}
              </div>
            )}

            {result && (
              <div>
                <textarea
                  readOnly
                  value={result.response}
                  rows={12}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: `1px solid ${COLORS.border}`,
                    fontSize: 12,
                    fontFamily: "'Courier New', Courier, monospace",
                    background: COLORS.pageBg,
                    color: COLORS.dark,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    lineHeight: 1.6,
                  }}
                />
                <div style={{ fontSize: 11, color: COLORS.mid, marginTop: 6 }}>
                  {result.tokens.toLocaleString()} tokens used
                  {' · '}
                  {result.documentsProcessed} document
                  {result.documentsProcessed !== 1 ? 's' : ''} processed
                </div>
              </div>
            )}
          </>
        )}

      </div>

      <GenerateDocumentModal
        baseApiUrl={baseApiUrl}
        open={generatePrompt !== null}
        prompt={generatePrompt}
        entityId={entityId}
        jobId={jobId}
        onClose={() => setGeneratePrompt(null)}
        onSuccess={(result) => {
          showToast(`Saved ${result.fileName}`);
          if (result.sharePointUrl) {
            window.open(result.sharePointUrl, '_blank', 'noopener,noreferrer');
          }
          setGeneratePrompt(null);
        }}
        onSubmitted={() => {
          showToast('Generation queued — view progress in AI Dispatches');
          setGeneratePrompt(null);
        }}
      />

      {toastNode}
    </div>
  );
}
